// DiscoveryButtons
// =================
//
// Wave 14ce-glove2: 2 Tasten (Block + One-way) für Handschuh-Bedienung.
// Direction-Auswahl beim One-way: explizite Auswahl per Modal mit den
// zwei kompass-orientierten Optionen. App ermittelt vorher, ob der Way
// eher N-S oder eher O-W ausgerichtet ist, und zeigt entsprechend:
//
//   N-S-Way  →  [ ↓ Nord nach Süd ]   [ ↑ Süd nach Nord ]
//   O-W-Way  →  [ → West nach Ost ]   [ ← Ost nach West ]
//
// User entscheidet, welche Richtung erlaubt ist. Mapping zu Backend-
// Status:
//   - Way (snap.from → snap.to) Richtung gewählt → one_way_forward
//   - Gegenrichtung gewählt                       → one_way_reverse
//
// Block bleibt single-tap (eine Aktion, keine Richtung nötig).
//
// Wave 14ca-fix64: Dritter Button "SCHLÜSSEL" fuer punkt-basierte
// Schluesselstellen. Anders als Block/One-way (way_overrides → routing-
// relevant, brauchen admin_light_plus) braucht Schluesselstellen nur
// admin_light — Light-Admins sollen Gefahrenstellen melden duerfen, ohne
// Routing-Aenderungen zu verursachen. Daher Role-Prop: Button-Sichtbar-
// keit per Rolle steuern, statt das ganze Component zu gaten.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { positionBus } from '../lib/positionBus';
import {
  markCruxSpot,
  markWayOverride,
  snapPosition,
  type SnapResult,
} from '../lib/discoveryApi';
import { roleAtLeast, type RiderRole } from '../lib/auth';
// Wave 14ca-fix64: WAY-ID-Trigger lebt jetzt standalone in MapPage,
// daher hier nicht mehr importiert.

// ─── Bearing-Helpers ────────────────────────────────────────────────────
function bearingDeg(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Klassifiziert die Hauptachse eines Ways (N-S vs O-W) und gibt für die
 *  beiden Endpunkte die jeweilige Himmelsrichtungs-Beschriftung zurück. */
function classifyAxis(snap: SnapResult): {
  axis: 'NS' | 'EW';
  /** „Forward" = von snap.from nach snap.to */
  forwardLabel: string;
  reverseLabel: string;
  forwardArrow: string;
  reverseArrow: string;
} {
  const b = bearingDeg(snap.fromLat, snap.fromLon, snap.toLat, snap.toLon);
  // |cos(b)| > |sin(b)| → N-S-dominant. cos = north-component, sin = east.
  const rad = (b * Math.PI) / 180;
  const ns = Math.abs(Math.cos(rad)) >= Math.abs(Math.sin(rad));
  if (ns) {
    // Way verläuft hauptsächlich Nord-Süd.
    const fromIsNorth = snap.fromLat > snap.toLat;
    return fromIsNorth
      ? {
          axis: 'NS',
          forwardLabel: 'North → South',
          reverseLabel: 'South → North',
          forwardArrow: '↓',
          reverseArrow: '↑',
        }
      : {
          axis: 'NS',
          forwardLabel: 'South → North',
          reverseLabel: 'North → South',
          forwardArrow: '↑',
          reverseArrow: '↓',
        };
  }
  // Way verläuft hauptsächlich Ost-West.
  const fromIsEast = snap.fromLon > snap.toLon;
  return fromIsEast
    ? {
        axis: 'EW',
        forwardLabel: 'East → West',
        reverseLabel: 'West → East',
        forwardArrow: '←',
        reverseArrow: '→',
      }
    : {
        axis: 'EW',
        forwardLabel: 'West → East',
        reverseLabel: 'East → West',
        forwardArrow: '→',
        reverseArrow: '←',
      };
}

// ─── Komponente ─────────────────────────────────────────────────────────

type PendingOneWay = {
  snap: SnapResult;
  axis: ReturnType<typeof classifyAxis>;
};

export interface DiscoveryButtonsProps {
  /** Role des eingeloggten Users — steuert welche Buttons angezeigt werden.
   *  - admin_light: nur SCHLÜSSEL (Crux-Spot)
   *  - admin_light_plus / admin: BLOCK + ONE-WAY + SCHLÜSSEL
   *  - undefined / 'user': nichts (sollte nie passieren — Caller gated, aber
   *    defensiv).
   */
  role?: RiderRole | null;
}

export function DiscoveryButtons({ role }: DiscoveryButtonsProps = {}) {
  const [busy, setBusy] = useState<'block' | 'oneway' | 'crux' | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingOneWay | null>(null);

  // Wave 14ca-fix64: Buttons rollen-abhaengig anzeigen.
  // Block + ONE-WAY brauchen admin_light_plus (way_overrides = Routing).
  // SCHLÜSSEL braucht admin_light (Marker, kein Routing-Effekt).
  const canRouting = roleAtLeast(role ?? null, 'admin_light_plus');
  const canCrux = roleAtLeast(role ?? null, 'admin_light');

  const handleBlock = async () => {
    setBusy('block');
    setFeedback(null);
    try {
      const pos = positionBus.get();
      if (!pos) {
        setFeedback('No GPS fix');
        return;
      }
      const snap = await snapPosition(pos.latitude, pos.longitude, 30);
      if (!snap?.wayId) {
        setFeedback('No OSM way nearby');
        return;
      }
      const res = await markWayOverride({
        osmWayId: snap.wayId,
        status: 'blocked',
        source: 'manual',
      });
      setFeedback(
        res.ok
          ? `✓ Blocked (way ${snap.wayId})`
          : `Failed: ${res.error || 'unknown'}`,
      );
    } finally {
      setBusy(null);
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  /** Schritt 1 von 2: snappen, Achse bestimmen, Modal öffnen. */
  const openOneWaySheet = async () => {
    setBusy('oneway');
    setFeedback(null);
    try {
      const pos = positionBus.get();
      if (!pos) {
        setFeedback('No GPS fix');
        return;
      }
      const snap = await snapPosition(pos.latitude, pos.longitude, 30);
      if (!snap?.wayId) {
        setFeedback('No OSM way nearby');
        return;
      }
      setPending({ snap, axis: classifyAxis(snap) });
    } finally {
      setBusy(null);
    }
  };

  /** Schluesselstelle am aktuellen GPS-Fix anlegen. Punkt-basiert, kein
   *  OSM-snap noetig — der Spot lebt unabhaengig vom Way-Graph. */
  const handleCrux = async () => {
    setBusy('crux');
    setFeedback(null);
    try {
      const pos = positionBus.get();
      if (!pos) {
        setFeedback('No GPS fix');
        return;
      }
      // Optional Note. window.prompt analog AddCruxHereButton — kein Modal-
      // Stack, im Feld mit Handschuhen schneller. null = Abbruch.
      const note = window.prompt(
        'Key section — short note (optional, e.g. "Root carpet after the switchback"):',
        '',
      );
      if (note === null) return;
      const ele =
        typeof pos.altitude === 'number' && Number.isFinite(pos.altitude)
          ? pos.altitude
          : null;
      const res = await markCruxSpot({
        lat: pos.latitude,
        lon: pos.longitude,
        ele,
        note: note.trim() || null,
      });
      setFeedback(
        res.ok
          ? `✓ Schlüsselstelle gespeichert`
          : `Failed: ${res.error || 'unknown'}`,
      );
    } finally {
      setBusy(null);
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  /** Schritt 2 von 2: User hat eine Richtung gewählt → POSTen. */
  const confirmOneWay = async (direction: 'forward' | 'reverse') => {
    if (!pending) return;
    const { snap, axis } = pending;
    setPending(null);
    setBusy('oneway');
    try {
      const status =
        direction === 'forward' ? 'one_way_forward' : 'one_way_reverse';
      const res = await markWayOverride({
        osmWayId: snap.wayId!,
        status,
        source: 'manual',
      });
      const label =
        direction === 'forward' ? axis.forwardLabel : axis.reverseLabel;
      setFeedback(
        res.ok
          ? `✓ One-way ${label} (way ${snap.wayId})`
          : `Failed: ${res.error || 'unknown'}`,
      );
    } finally {
      setBusy(null);
      setTimeout(() => setFeedback(null), 3500);
    }
  };

  // Wenn kein Button anwendbar ist (z.B. role='user'), gar nichts rendern.
  if (!canRouting && !canCrux) return null;

  return (
    <>
      <div className="flex flex-col items-stretch gap-1.5">
        {canRouting && (
          <button
            type="button"
            onClick={handleBlock}
            disabled={busy !== null}
            className="w-24 h-14 rounded-xl bg-red-500/90 hover:bg-red-400 active:bg-red-600 text-white text-[13px] font-bold leading-none flex flex-col items-center justify-center gap-0.5 border border-red-300/40 shadow-md disabled:opacity-50 transition-colors"
            title="Mark current OSM way as blocked"
            aria-label="Block this way"
          >
            <span aria-hidden="true" className="text-lg leading-none">🚫</span>
            <span className="tracking-wide">{busy === 'block' ? '…' : 'BLOCK'}</span>
          </button>
        )}
        {canRouting && (
          <button
            type="button"
            onClick={openOneWaySheet}
            disabled={busy !== null}
            className="w-24 h-14 rounded-xl bg-amber-500/90 hover:bg-amber-400 active:bg-amber-600 text-black text-[13px] font-bold leading-none flex flex-col items-center justify-center gap-0.5 border border-amber-300/50 shadow-md disabled:opacity-50 transition-colors"
            title="Mark current OSM way as one-way (you'll pick the allowed direction)"
            aria-label="Mark one-way (pick direction)"
          >
            <span aria-hidden="true" className="text-lg leading-none">↦</span>
            <span className="tracking-wide">
              {busy === 'oneway' && !pending ? '…' : 'ONE-WAY'}
            </span>
          </button>
        )}
        {/* Wave 14ca-fix64: SCHLÜSSEL — punkt-basierter Schluesselstellen-
            Marker. Sichtbar fuer admin_light + hoeher. Visuell ein
            Hazard-Triangle damit's auf einen Blick als "Gefahrenstelle"
            lesbar ist; Farbe Amber/Gelb (Warn-Code) um sich von BLOCK
            (rot) und ONE-WAY (orange) abzusetzen. */}
        {canCrux && (
          <button
            type="button"
            onClick={handleCrux}
            disabled={busy !== null}
            className="w-24 h-14 rounded-xl bg-yellow-400/95 hover:bg-yellow-300 active:bg-yellow-500 text-black text-[13px] font-bold leading-none flex flex-col items-center justify-center gap-0.5 border border-yellow-200/60 shadow-md disabled:opacity-50 transition-colors"
            title="Mark a key section at the current GPS point"
            aria-label="Mark key section here"
          >
            <span aria-hidden="true" className="text-lg leading-none">⚠️</span>
            <span className="tracking-wide">
              {busy === 'crux' ? '…' : 'KEY'}
            </span>
          </button>
        )}
        {/* Wave 14ca-fix64: WAY-ID war frueher hier im Discovery-Stack.
            User-Wunsch: "WAY ID Button mega sinnvoll, sollte immer da sein
            wenn ich es als admin aktiviert habe — die anderen (BLOCK /
            ONE-WAY / SCHLÜSSEL) halt nur beim RIDE". Daher rausgezogen,
            wird jetzt standalone in MapPage gerendert (siehe
            WayPickStandaloneButton, sichtbar unabhaengig vom Recording-
            State solange adminMode + discoveryMode aktiv sind). */}
        {feedback && (
          <span className="mt-0.5 text-[11px] text-white bg-turbo-900/80 px-2 py-1 rounded-md leading-snug max-w-[12rem]">
            {feedback}
          </span>
        )}
      </div>

      {pending &&
        createPortal(
          <OneWayPicker
            axis={pending.axis}
            wayId={pending.snap.wayId!}
            onPick={confirmOneWay}
            onCancel={() => setPending(null)}
          />,
          document.body,
        )}
    </>
  );
}

/** Modal-Sheet für die Richtungs-Wahl. Zwei grosse Tasten + Cancel,
 *  alles glove-tauglich (≥ 64 px hoch). Backdrop schliesst.
 *  Wir benutzen ein Portal damit der Modal nicht durch die Map-z-Hierarchie
 *  geclipped wird. */
function OneWayPicker({
  axis,
  wayId,
  onPick,
  onCancel,
}: {
  axis: ReturnType<typeof classifyAxis>;
  wayId: string;
  onPick: (direction: 'forward' | 'reverse') => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose one-way direction"
      onClick={onCancel}
    >
      <div
        className="bg-turbo-900 border border-turbo-500/40 rounded-2xl p-5 w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white mb-1">One-way direction</h3>
        <p className="text-[12px] text-white/60 mb-4 leading-snug">
          Way {wayId} runs {axis.axis === 'NS' ? 'north-south' : 'east-west'}.
          Choose the <span className="text-amber-300">allowed</span> direction of travel.
        </p>
        <div className="space-y-2.5">
          <button
            type="button"
            onClick={() => onPick('forward')}
            className="w-full h-16 rounded-xl bg-amber-500/90 hover:bg-amber-400 active:bg-amber-600 text-black font-bold text-base flex items-center justify-center gap-3 border border-amber-300/50 shadow"
          >
            <span aria-hidden="true" className="text-3xl leading-none">
              {axis.forwardArrow}
            </span>
            <span>{axis.forwardLabel}</span>
          </button>
          <button
            type="button"
            onClick={() => onPick('reverse')}
            className="w-full h-16 rounded-xl bg-amber-500/90 hover:bg-amber-400 active:bg-amber-600 text-black font-bold text-base flex items-center justify-center gap-3 border border-amber-300/50 shadow"
          >
            <span aria-hidden="true" className="text-3xl leading-none">
              {axis.reverseArrow}
            </span>
            <span>{axis.reverseLabel}</span>
          </button>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 w-full h-12 rounded-xl bg-turbo-700/60 hover:bg-turbo-700 text-white/80 hover:text-white font-medium text-sm border border-white/10"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
